'use client'

import { Clock, Mail, MessagesSquare, type LucideIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@elmnts/ui/tooltip'
import { ChannelAvatar } from '@/components/ChannelAvatar'
import { ChannelPresence } from '@/components/ChannelPresenceAvatars/ChannelPresence'
import { COUNTER_TOOLTIPS, counterAriaLabel, type CounterKey } from '@/lib/format/channelCounters'
import { cn } from '@/lib/utils'
import type { IChannelInfo } from '@/providers/ChatBotContext'

interface ChannelCardProps {
  channel: IChannelInfo
  onSelect: (dbId: string) => void
}

/** Um contador: ícone + número, com o nome do número no tooltip e no `aria-label`.
 *  Sem rótulo em texto — foi rótulo ("aguardando", "em atendimento") que produziu a
 *  semântica falsa da tela antiga. */
function Counter({
  icon: Icon,
  counterKey,
  value,
  highlight = false,
}: {
  icon: LucideIcon
  counterKey: CounterKey
  value: number
  highlight?: boolean
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={counterAriaLabel(counterKey, value)}
          className={cn(
            'inline-flex items-center gap-1 tabular-nums',
            highlight ? 'font-semibold text-brand-accent' : 'text-muted-foreground',
          )}
        >
          <Icon aria-hidden="true" className="size-3.5" />
          {value}
        </span>
      </TooltipTrigger>
      <TooltipContent>{COUNTER_TOOLTIPS[counterKey]}</TooltipContent>
    </Tooltip>
  )
}

/**
 * Card de canal da home. O card INTEIRO é o alvo de clique — por isso não há
 * texto de ação ("Acessar →"): ele ocupava uma linha em cada card, repetia a
 * mesma palavra quatro vezes e não informava nada que o card já não dissesse.
 * A affordance fica no hover/focus.
 */
export function ChannelCard({ channel, onSelect }: ChannelCardProps) {
  const unread = channel.total_unread_messages
  const waiting = channel.waiting_chats
  const open = channel.chats_count
  const idle = unread === 0 && waiting === 0 && open === 0

  return (
    <button
      type="button"
      onClick={() => onSelect(channel.db_id)}
      className="group relative flex h-full w-full flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-card p-5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-white/20 hover:shadow-xl hover:shadow-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="flex items-center gap-3">
        <ChannelAvatar icon={channel.icon} color={channel.color} size="md" />
        <span className="min-w-0 flex-1 truncate text-[0.9375rem] font-semibold tracking-tight text-foreground">
          {channel.name}
        </span>
      </div>

      <p className="line-clamp-2 flex-1 text-[0.8125rem] leading-relaxed text-muted-foreground">
        {channel.description || 'Sem descrição disponível.'}
      </p>

      {/* Só o que tem novidade chama atenção: contador zerado é OMITIDO, e com os três
          zerados o card diz isso em uma palavra. Na bancada real, 3 de 5 cards exibiam
          "0 em atendimento" — contagem zero não é dado, é ruído.
          `text-brand-accent` e NUNCA `text-primary` no destaque: o rosa da marca
          reprova o AA nos dois temas (ver ChannelCard.md). */}
      <div className="flex items-center gap-3 border-t border-border/60 pt-3 text-xs">
        {idle ? (
          <span className="text-muted-foreground/70">Sem conversas</span>
        ) : (
          <>
            {unread > 0 && (
              <Counter icon={Mail} counterKey="total_unread_messages" value={unread} highlight />
            )}
            {waiting > 0 && <Counter icon={Clock} counterKey="waiting_chats" value={waiting} />}
            {open > 0 && <Counter icon={MessagesSquare} counterKey="chats_count" value={open} />}
          </>
        )}
        {/* Quem está no canal agora, à direita — inclusive no "Sem conversas": canal parado
            não diz nada sobre quem está olhando. Sem as três permissions a lista vem vazia e
            nada renderiza. Não-interativo: o card inteiro já é o <button>. */}
        <ChannelPresence channelId={channel.db_id} className="ml-auto" />
      </div>
    </button>
  )
}
